import React from "react";


import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

const RELATED_FETCH_LIMIT = 5000;
const LIST_DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

const SEARCH_FIELDS = ["code", "name", "address"];

const uniqNonNull = (values) => {
  const unique = new Set();

  for (const value of values || []) {
    if (value !== null && value !== undefined && value !== "") {
      unique.add(value);
    }
  }

  return Array.from(unique);
};

const quoteRsqlValue = (value) => {
  if (typeof value === "number") {
    return String(value);
  }

  return `"${String(value).replaceAll('"', '\\"')}"`;
};

const rsqlIn = (selector, values) => `${selector}=in=(${values.map(quoteRsqlValue).join(",")})`;

const rsqlAnd = (parts) => parts.map((part) => String(part || "").trim()).filter(Boolean).join(";");

const escapeRsqlRegexValue = (value) => {
  return String(value || "")
    .trim()
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const buildSearchQuery = (search) => {
  const value = escapeRsqlRegexValue(search);

  if (!value) {
    return "";
  }

  return SEARCH_FIELDS
    .map((field) => `${field}=re="(?i).*${value}.*"`)
    .join(",");
};

const readRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit = Number.isFinite(limit) && limit > 0
    ? Math.floor(limit)
    : LIST_DEFAULT_PAGE_SIZE;

  const safeOffset = Number.isFinite(offset) && offset >= 0
    ? Math.floor(offset)
    : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit")
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  window.history.replaceState({}, "", url.toString());
};

const getEffectiveQuery = (query) => {
  const search = String(query?.search || "").trim();
  const sort = Array.isArray(query?.sort) ? query.sort : [];
  const page = Number.isFinite(query?.page) ? query.page : 0;
  const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : LIST_DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    page === 0 &&
    pageSize === LIST_DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;

    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      sort
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [column.sortSelector];
  }

  if (sortItem?.field) {
    return [sortItem.field];
  }

  throw new Error("Invalid sort item: missing sort selector");
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (sortItem?.direction !== "asc" && sortItem?.direction !== "desc") {
      throw new Error(`Invalid sort direction: ${sortItem?.direction}`);
    }

    for (const selector of selectorsForSortItem(sortItem)) {
      if (!selector) {
        throw new Error(`Invalid sort selector for field: ${sortItem?.field}`);
      }

      out.push({ selector, direction: sortItem.direction });
    }
  }

  return out;
};

const buildPayload = (range) => {
  const sort = buildSortPayload(range.sort);

  return {
    columns: [],
    query: rsqlAnd([buildSearchQuery(range.search)]) || null,
    limit: range.limit,
    offset: range.offset,
    ...(sort.length ? { sort } : {})
  };
};

const indexByGid = (rows) => {
  const byGid = new Map();

  for (const row of rows || []) {
    if (row.gid !== null && row.gid !== undefined) {
      byGid.set(row.gid, row);
    }
  }

  return byGid;
};

const pickLabel = (row) => {
  if (!row) {
    return "";
  }

  return row.name || row.label || row.code || (row.gid !== null && row.gid !== undefined ? String(row.gid) : "");
};

const pickVotingCenterName = (row) => row.name || row.code || row.label || "";

const loadRelatedRows = async (votingCenters) => {
  const sectionGids = uniqNonNull((votingCenters || []).map((center) => center.sectionGid ?? center.section_gid));

  const sections = sectionGids.length
    ? await rest.request(getServiceUri() + "section/search", "POST", {
        columns: [],
        query: rsqlIn("gid", sectionGids),
        limit: RELATED_FETCH_LIMIT,
        offset: 0
    })
    : [];

  return {
    sectionByGid: indexByGid(sections)
  };
};

const enrichVotingCenterRows = async (votingCenters) => {
  const { sectionByGid } = await loadRelatedRows(votingCenters);

  return (votingCenters || []).map((center) => {
    const sectionGid = center.sectionGid ?? center.section_gid;
    const section = sectionByGid.get(sectionGid);

    return {
      ...center,
      gid: center.gid ?? "",
      code: center.code ?? "",
      name: pickVotingCenterName(center),
      sectionGid,
      sectionLabel: pickLabel(section),
      address: center.address ?? ""
    };
  });
};

const votingCenterListProjection = {
  storageKey: "haiti.voting-center",

  columns: [
    {
      title: "common.code",
      field: "code",
      sortSelector: "code"
    },

    {
      title: "common.name",
      field: "name",
      sortSelector: "name",
      render: (row) => row.name || null
    },

    {
      title: "person.section",
      field: "sectionLabel",
      sorting: false,
      filtering: false,
      render: (row) => row.sectionLabel || null
    },

    {
      title: "common.address",
      field: "address",
      sortSelector: "address",
      render: (row) => row.address || null
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: () => null,

  fetch: async (query) => {
    const range = getEffectiveQuery(query);

    writeRangeToUrl({ offset: range.offset, limit: range.limit });

    const payload = buildPayload(range);

    const votingCenters = await rest.request(getServiceUri() + "voting-center/search", "POST", payload);

    const totalCount = await rest.request(getServiceUri() + "voting-center/count", "POST", {
      columns: [],
      query: payload.query,
      limit: null,
      offset: null
    });

    return {
      data: await enrichVotingCenterRows(votingCenters || []),
      totalCount,
      page: range.page
    };
  }
};

export const displayVotingCenterList = (gui) => {
  initialRange = readRangeFromUrl();
  initialRangeUsesLeft = initialRange.hasExplicitRange ? 3 : 0;

  return displayList(gui, votingCenterListProjection);
};

export default displayVotingCenterList;
